import { Medium } from "@/components/app/editor/media/Medium";
import { MediumCreateDialog } from "@/components/app/editor/media/MediumCreateDialog";
import { useEnable } from "@/hooks/useEnable";
import { editorClient } from "@/lib/editorClient";
import { Trans } from "@lingui/react/macro";
import { Button } from "@op/sf-editor";
import { usePublished } from "@op/utils";
import { FeatherUpload } from "@subframe/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { redirect } from "@tanstack/react-router";
import { useMemo, useState } from "react";

export const Route = createFileRoute(
  "/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/$versionId/media/"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { versionId, organizationSlug, projectSlug } = Route.useParams();
  const queryClient = useQueryClient();

  const [mediumCreateDialogOpen, setMediumCreateDialogOpen] =
    useState<boolean>(false);

  const published = usePublished();
  const canCreate = useEnable(organizationSlug, projectSlug, [
    "admin",
    "publisher",
    "editor",
  ]);

  const createDisabled = useMemo(() => {
    return !canCreate || published;
  }, [canCreate, published]);

  const { data: media } = useQuery(
    {
      queryKey: ["editor-version-media", { versionId }],
      queryFn: async () => {
        const res = await editorClient.versions[":versionId"].media.$get({
          param: {
            versionId,
          },
        });
        if (!res.ok) {
          if (res.status === 401) throw redirect({ to: "/login" });
          throw new Error("Failed to get media");
        }
        const data = await res.json();
        return data;
      },
    },
    queryClient
  );

  return (
    <div className="flex w-full flex-col items-start gap-6 px-4 mx-auto max-w-[1280px]">
      <div className="flex w-full items-center gap-6 justify-end">
        <Button
          disabled={createDisabled}
          variant="brand-secondary"
          size="small"
          icon={<FeatherUpload />}
          iconRight={null}
          loading={false}
          onClick={() => {
            setMediumCreateDialogOpen(true);
          }}
        >
          <Trans>Create Medium</Trans>
        </Button>
      </div>
      <div className="w-full flex-col items-start gap-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {media?.map((medium) => {
          return (
            <Medium
              key={medium.id}
              id={medium.id}
              versionId={versionId}
              organizationSlug={organizationSlug}
              projectSlug={projectSlug}
              uploadId={medium.uploadId}
              filename={medium.filename}
              metadata={medium.metadata}
              mimeType={medium.mimeType}
              width={medium.width}
              height={medium.height}
            />
          );
        })}
      </div>
      <MediumCreateDialog
        open={mediumCreateDialogOpen}
        onOpenChange={setMediumCreateDialogOpen}
        versionId={versionId}
      />
    </div>
  );
}
